<script setup>
// eslint-disable-next-line import/no-restricted-paths -- TODO: move out of DS once password-branch merges
import { i18n } from 'boot/i18n';
// eslint-disable-next-line import/no-restricted-paths, import/no-cycle -- TODO:move out of DS on password-branch merges
import { usePasswordValidation } from 'src/composables/passwordValidation';
import { computed, ref, useAttrs, watch } from 'vue';

import { HrIcon, HrTextField } from '../primitives';

/**
 * Password field — an HrTextField with a visibility toggle and an optional 3-segment strength indicator.
 *
 * All unrecognized attrs (`label`, `placeholder`, `v-model`, …) are forwarded to the underlying HrTextField.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Collecting a password with optional strength feedback during sign-up or password change flows
 * @useWhen Confirming a password — pass the original as `confirm` for the match rule and re-validation
 * @event update:modelValue Forwarded from the underlying HrTextField; new string value.
 */
// inheritAttrs: false because the template spreads $attrs itself. With inheritance left on, the
// fallthrough is re-applied after the explicit bindings, so a caller's `rules` silently replaced
// the built-in validation and match rules instead of adding to them.
defineOptions({ name: 'HrPasswordField', inheritAttrs: false });

const props = defineProps({
  /** Applies password strength/format validation rules to the field when true. */
  validate: {
    type: Boolean,
    default: false,
  },
  /** Renders a 3-segment strength bar and label below the field when true. */
  strengthIndicator: {
    type: Boolean,
    default: false,
  },
  /**
   * The user's email address. Passed to validation rules and the strength scorer
   * so that passwords containing the email are penalised.
   */
  email: {
    type: String,
    default: null,
  },
  /**
   * The password this field must match, turning it into the confirmation half of a pair.
   * Adds the match rule and re-checks it whenever the original changes, so an already-confirmed
   * field cannot keep a stale pass after the original is edited.
   */
  confirm: {
    type: String,
    default: null,
  },
});

const { getPasswordRules, evaluatePasswordStrength } = usePasswordValidation();
const attrs = useAttrs();

const visible = ref(false);
const fieldRef = ref(null);

// v-model lands in attrs because inheritAttrs is false — we can't declare it as a prop
// without breaking the pass-through behavior to HrTextField.
const password = computed(() => attrs.modelValue);

const matchesConfirm = (value) => value === props.confirm
  || i18n.t('signup.signupComponent.inputConfirmPasswordValidator');

// Caller rules come last so a screen can bolt on its own without losing the built-in ones.
const rules = computed(() => [
  ...(props.validate ? getPasswordRules(props.email) : []),
  ...(props.confirm === null ? [] : [matchesConfirm]),
  ...(attrs.rules ?? []),
]);

// This field's validity depends on a value it doesn't own, so Quasar has nothing to react to
// when the original password changes — without this an already-confirmed field keeps its pass.
watch(() => props.confirm, () => {
  if (password.value) {
    fieldRef.value?.validate();
  } else {
    fieldRef.value?.resetValidation();
  }
});

const passwordScore = computed(() => (
  password.value ? evaluatePasswordStrength(password.value, props.email) : null
));

// Thresholds: 100 → strong (3 segments), ≥ 50 → fair (2), < 50 → weak (1)
const activeSegments = computed(() => {
  if (passwordScore.value === null) return 0;
  if (passwordScore.value === 100) return 3;
  if (passwordScore.value >= 50) return 2;
  return 1;
});

const strengthClass = computed(() => {
  if (passwordScore.value === 100) return 'strength-indicator--strong';
  if (passwordScore.value >= 50) return 'strength-indicator--fair';
  return 'strength-indicator--weak';
});

const strengthLabel = computed(() => {
  const base = 'signup.signupComponent.inputPasswordValidator';
  if (passwordScore.value === 100) return i18n.t(`${base}.strongStrengthIndicatorText`);
  if (passwordScore.value >= 50) return i18n.t(`${base}.moderateStrengthIndicatorText`);
  return i18n.t(`${base}.weakStrengthIndicatorText`);
});
</script>

<template>
  <hr-text-field
    ref="fieldRef"
    v-bind="$attrs"
    :class="strengthIndicator ? 'hr-password-field--with-strength' : null"
    :type="visible ? 'text' : 'password'"
    :rules="rules"
    lazy-rules
    @update:model-value="fieldRef?.resetValidation()"
  >
    <template #append>
      <hr-icon
        :name="visible ? 'visibility' : 'visibility_off'"
        class="cursor-pointer"
        @click="visible = !visible"
      />
    </template>
    <template
      v-if="strengthIndicator"
      #hint
    >
      <div
        class="strength-indicator"
        :class="password ? strengthClass : null"
      >
        <div class="strength-track">
          <div
            v-for="n in 3"
            :key="n"
            class="strength-segment"
            :class="{
              'strength-segment--left': n === 1,
              'strength-segment--right': n === 3,
              'strength-segment--active': n <= activeSegments,
            }"
          />
        </div>
        <span
          v-if="password"
          class="strength-label"
        >
          {{ strengthLabel }}
        </span>
      </div>
    </template>
  </hr-text-field>
</template>

<style lang="scss" scoped>
// .q-field__bottom is absolutely positioned, so it adds no height — Quasar instead reserves a fixed
// 20px via .q-field--with-bottom. The indicator needs 24px (8px Quasar padding + 4px + the 12px
// label), so without this it overlaps whatever follows the field. 32px mirrors Quasar's 8px above.
.hr-password-field--with-strength.q-field--with-bottom {
  padding-bottom: 32px;
}

// The strength indicator lives in Quasar's #hint slot. On blur, a failing rule
// swaps hint → error inside a <transition> whose leave state sets
// .q-field__messages to position: absolute, dropping it out of the flex row so it
// shrinks to content — the flex-1 track collapses and the label jumps left for the
// duration of the fade-out. We can't restore the width of a detached, leaving node,
// so hide the indicator the moment it starts leaving; the error message just fades
// in cleanly in its place.
:deep(.q-field__messages.q-transition--field-message-leave-active .strength-indicator) {
  display: none;
}

// The label is the tallest item in the row, so without a reserved line box the 4px track
// re-centers and the whole indicator drops as soon as the label appears on the first keystroke.
// box-sizing is border-box globally, so the padding has to be part of the reserved height.
$strength-label-line-box: 12px;
$strength-indicator-padding-top: 4px;

.strength-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: $strength-indicator-padding-top;
  min-height: $strength-indicator-padding-top + $strength-label-line-box;
}

.strength-track {
  display: flex;
  flex: 1;
  gap: 3px;
}

.strength-segment {
  position: relative;
  overflow: hidden;
  flex: 1;
  height: 4px;
  border-radius: 0;
  background-color: var(--app-surface-variant);

  &--left { border-radius: 2px 0 0 2px; }
  &--right { border-radius: 0 2px 2px 0; }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 0.3s ease, background-color 0.25s ease;
  }

  &--active::after { transform: scaleX(1); }

  // Stagger only on fill (delay lives on --active, so drain has no delay)
  &--active:nth-child(2)::after { transition-delay: 0.05s; }
  &--active:nth-child(3)::after { transition-delay: 0.1s; }
}

.strength-indicator {
  &--weak {
    .strength-segment::after { background-color: var(--app-error); }
    .strength-label { color: var(--app-error); }
  }

  &--fair {
    .strength-segment::after { background-color: var(--app-warning); }
    .strength-label { color: var(--app-warning); }
  }

  &--strong {
    .strength-segment::after { background-color: var(--app-success); }
    .strength-label { color: var(--app-success); }
  }
}

.strength-label {
  font-size: $strength-label-line-box;
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.25s ease;
}
</style>
